import { addDays, addMonths, format } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentFrequency, EProductsInsurance } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { getMinMaxPaymentDates } from '@src/services/paymentDate-utils'
import { min } from 'lodash-es'
import { baseWorksheetSchema } from './BaseWorksheetSchema'
import {
  IFinanceBaseWorksheet,
  IFinanceBaseWorksheetDecisionDTOSchema,
  IFinanceBaseWorksheetRevision,
} from './IFinanceBaseWorksheetSchema'
import { MerchantPayment } from './MerchantPayment'

const MIN_TERM = 12

export const baseInsuranceSchema = yup.object({
  provider: yup.string().nullable().default(null),
  police: yup.string().nullable().default(null),
  term: yup.integer().min(MIN_TERM),
  amount: yup.double(),
  endDate: yup.string().nullable().default(null).dateOnly(),
})

const withConditionalEndDate = (schema: typeof baseInsuranceSchema, isRequired: boolean) => {
  if (isRequired) {
    return schema.shape({
      endDate: yup
        .string()
        .dateOnly()
        .required()
        .when('term', ([term], schema) => {
          if (term && !isNaN(Number(term))) {
            const date = new Date()
            const minDate = format(addMonths(date, Number(term)), 'yyyy-MM-dd')
            const maxDate = format(addDays(addMonths(date, Number(term)), 21), 'yyyy-MM-dd')

            return schema.default(minDate).test('min-date', (val, ctx) => {
              if (!val) return true
              if (val < minDate) {
                return ctx.createError({
                  message: t('common.errors.invalidMinDate', { minValue: minDate }),
                })
              }
              if (val > maxDate) {
                return ctx.createError({
                  message: t('common.errors.invalidMaxDate', { maxValue: maxDate }),
                })
              }
              return true
            })
          }
          return schema.default(format(new Date(), 'yyyy-MM-dd'))
        }),
    })
  }
  return schema
}

export type ProductsInsurance = yup.InferType<typeof baseInsuranceSchema>

export type ProductsInsurancePlan = ProductsInsurance & {
  providerName: string
  type: EProductsInsurance
}

const resolveInsuranceMaxTerm = (schema: yup.ObjectSchema<ProductsInsurance>, vehicleFinancingTerm: number) => {
  if (vehicleFinancingTerm)
    (schema.fields.term as yup.NumberSchema<number | null | undefined, yup.AnyObject, null, 'd'>).max(
      vehicleFinancingTerm,
    )

  return schema
}

const resolveMaxTerm = (schema: yup.NumberSchema<number | undefined>, values: ProductsInsurance[]) => {
  const terms: number[] = []

  values.forEach((e) => {
    if (e) if (e.term) terms.push(e.term)
  })

  const max = min(terms)

  if (max) {
    return schema.max(max)
  }

  return schema
}
export const buildProductsWorksheetDtoSchema = (
  replacementOrGapInsuranceTerm: boolean,
  creditInsuranceTerm: boolean,
  extendedWarrantyTerm: boolean,
) => {
  const productsWorksheetSchema = yup
    .object({
      creditApplicationId: yup.string(),
      amountRequested: yup.double().required().positive(),
      includeInsurance: yup.boolean().default(false),
      merchantPayments: yup.mixed<MerchantPayment[]>().default([]),
      paymentPlanId: yup.string().required(),
      deliveryOn: yup.string().validDate().default(new Date().toDateString()).nullable().required(),
      firstPaymentOn: yup
        .string()
        .dateOnly()
        .default(() => {
          const date = new Date()
          date.setDate(date.getDate() + 2)
          return format(date, 'yyyy-MM-dd')
        })
        .required()
        .test('deliveryOn-range', (val, ctx) => {
          const deliveryOn = (ctx.parent as ProductsWorksheet).deliveryOn
          const paymentFrequency = (ctx.parent as { paymentFrequency: EPaymentFrequency | null }).paymentFrequency
          if (!val || !deliveryOn || !paymentFrequency) return true
          const [minDate, maxDate] = getMinMaxPaymentDates(deliveryOn, paymentFrequency)
          if (val < formatDate(minDate))
            return ctx.createError({ message: t('common.errors.invalidMinDate', { minValue: formatDate(minDate) }) })
          if (val > formatDate(maxDate))
            return ctx.createError({ message: t('common.errors.invalidMaxDate', { maxValue: formatDate(maxDate) }) })
          return true
        }),
      term: (baseWorksheetSchema.fields.term as yup.NumberSchema)
        .required()
        .when(
          ['extendedWarranty', 'replacementOrGapInsurance', 'creditInsurance'],
          (values: ProductsInsurance[], schema: yup.NumberSchema<number | undefined>) => {
            return resolveMaxTerm(schema, values)
          },
        ),
      vehicleFinancingTerm: yup.integer().nullable().required().min(MIN_TERM).max(240),

      extendedWarranty: withConditionalEndDate(baseInsuranceSchema, extendedWarrantyTerm)
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),

      creditInsurance: withConditionalEndDate(baseInsuranceSchema, creditInsuranceTerm)
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),

      replacementOrGapInsurance: withConditionalEndDate(baseInsuranceSchema, replacementOrGapInsuranceTerm)
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),
    })
    .concat(baseWorksheetSchema.omit(['firstPaymentOn', 'lenderFee']))
  return productsWorksheetSchema
}

export type ProductsWorksheet = IFinanceBaseWorksheet & {
  creditApplicationId: string
  creditInsurance: ProductsInsurance
  replacementOrGapInsurance: ProductsInsurance
  extendedWarranty: ProductsInsurance
  vehicleFinancingTerm: number
}

export type ProductsWorksheetDto = yup.InferType<ReturnType<typeof buildProductsWorksheetDtoSchema>>

export type ProductsWorksheetRevision = IFinanceBaseWorksheetRevision

export type ProductsWorksheetDecisionDTO = yup.InferType<typeof IFinanceBaseWorksheetDecisionDTOSchema>
